import React from 'react'
import GoogleMapReact from 'google-map-react'
import useLanguage from '@hooks/useLanguage.hook'
import { useHistory, useParams } from 'react-router-dom'
import Marker from '@components/LotsMap/Marker'
import { ButtonOverrides, Container, MapContainer, Title } from './Occupancy.style'
import Button from 'parkdepot-shared/components/Buttons/Button'
import { BUTTON_KIND, BUTTON_USE } from 'parkdepot-shared/components/Buttons'
import useCarpayGetLotById from '@hooks/useGetCarpayLotById'
import OccupancyChart from '@components/OccupancyChart/OccupancyChart.component'
import Error from '@components/Error/Error.component'
import Spinner from 'parkdepot-shared/components/Spinner'
import { SpinnerContainer } from './Occupancy.style'
import useGetLotOccupancy from '@hooks/useGetLotOccupancy'
import { useTranslation } from 'react-i18next'

const Occupancy: React.FC = () => {
    const history = useHistory()
    const [lng] = useLanguage()
    const { t } = useTranslation()
    const { lotId } = useParams<{ lotId: string }>()

    const {
        data: lotData,
        loading: isLotLoading,
        error: isLotError
    } = useCarpayGetLotById({
        skip: !lotId,
        variables: { lot_id: lotId }
    })

    const { name, latitude, longitude } = lotData?.b2c_get_carpay_lot.lot || {}

    const {
        data: occupancyData,
        loading: isOccupancyLoading,
        error: isOccupancyError
    } = useGetLotOccupancy({
        skip: !lotId,
        variables: {
            lot_id: lotId
        },
        fetchPolicy: 'cache-and-network',
        pollInterval: 30000
    })

    const {
        free_spots: freeSpots,
        occupancy_distribution: occupancyDistribution,
        last_updated_at: lastUpdatedAt
    } = occupancyData?.b2c_get_lot_occupancy || {}

    const isBackButtonShown = history.length > 2

    if (!lotId || isLotError || isOccupancyError) return <Error />

    if (isLotLoading || isOccupancyLoading) {
        return (
            <SpinnerContainer>
                <Spinner size="lg" />
            </SpinnerContainer>
        )
    }

    return (
        <Container>
            {latitude && longitude && (
                <MapContainer>
                    <GoogleMapReact
                        bootstrapURLKeys={{
                            key: process.env.VITE_GOOGLE_MAPS_KEY as string,
                            language: lng
                        }}
                        center={{ lat: latitude, lng: longitude }}
                        defaultZoom={17}
                        options={{
                            disableDefaultUI: true,
                            draggable: false,
                            scrollwheel: false
                        }}
                    >
                        <Marker id={lotId} lat={latitude} lng={longitude} />
                    </GoogleMapReact>
                </MapContainer>
            )}
            <Title>
                <span>{name}</span>
                {isBackButtonShown && (
                    <Button
                        $kind={BUTTON_KIND.ghost}
                        $use={BUTTON_USE.icon}
                        onClick={() => history.goBack()}
                        overrides={ButtonOverrides}
                    >
                        <i className="wemolo-cross" aria-label={t('common.close')} />
                    </Button>
                )}
            </Title>

            <OccupancyChart
                freeSpots={freeSpots}
                occupancyDistribution={occupancyDistribution}
                lastUpdatedAt={lastUpdatedAt}
            />
        </Container>
    )
}

export default Occupancy
